import React, { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Formik } from 'formik';
import useUser from 'src/hooks/useUser';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import { useServiceClient } from 'src/hooks/useServiceClient';
import { CapitalizeNames } from 'src/utils/capitalize';
import { SERVICE_LEAD_TYPE } from 'src/constants/lead/leadTypes';
import { useTranslation } from 'react-i18next';
import useNotification from 'src/hooks/useNotification';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    TextField,
    FormHelperText,
} from '@material-ui/core';
import { User as EditIcon } from 'react-feather';
import { IAssignLead } from './types';
import { IUser } from 'src/interfaces/user';

const hasSelectedAgent = (selectedAgent: string) => Boolean(selectedAgent);

/**
 * Represent the component to assign a lead to an agent
 *
 * @param {*} {  ...props }
 * @return {*}
 */
export const AssignLead = ({
    area,
    carType,
    storeId,
    leadId,
    currentAgent,
    isPermissionGranted,
    ...props
}: IAssignLead) => {
    const { enqueueSnackbar } = useSnackbar();
    const { updateLead } = useLead();
    const { updateServiceClient } = useServiceClient();
    const { getAgents, agents } = useUser();
    const { createNotification } = useNotification();
    const { user } = useAuth();
    const { t } = useTranslation();

    const handleSearchAgents = () => {
        if (!storeId) return;

        getAgents({
            stores: storeId,
            carType: carType,
            areas: area,
            isPermissionGranted,
        });
    };

    useEffect(() => {
        handleSearchAgents();
    }, [leadId]);

    return (
        <Card {...props}>
            <CardHeader title={t('Leads.Agent')} />
            <Divider />
            <CardContent>
                <Formik
                    enableReinitialize
                    initialValues={{
                        agent: currentAgent || '',
                    }}
                    onSubmit={async (
                        values,
                        { resetForm, setErrors, setStatus, setSubmitting }
                    ) => {
                        if (!hasSelectedAgent(values.agent)) {
                            setSubmitting(false);
                            return;
                        }

                        try {
                            const payload: Record<string, unknown> = {
                                ...values,
                                assignedDate: new Date(),
                            };

                            if (area === SERVICE_LEAD_TYPE) {
                                await updateServiceClient(leadId, payload);
                            } else {
                                await updateLead(payload, leadId);
                            }

                            await createNotification({
                                to: values.agent,
                                type: 'assign',
                                store: storeId,
                                lead: leadId,
                                message: `${CapitalizeNames(
                                    user.name
                                )} has assigned you a lead`,
                            });

                            resetForm();
                            setStatus({ success: true });
                            setSubmitting(false);
                            enqueueSnackbar(t('SnackBar.AgentAssigned'), {
                                variant: 'success',
                            });
                        } catch (err) {
                            setStatus({ success: false });
                            if (err instanceof Error)
                                setErrors({ agent: err.message });

                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        errors,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        values,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                name="agent"
                                onChange={handleChange}
                                select
                                SelectProps={{ native: true }}
                                value={values.agent}
                                variant="outlined"
                            >
                                <option key={0} value={''}></option>
                                {agents.map((user: IUser) => (
                                    <option key={user._id} value={user._id}>
                                        {CapitalizeNames(user.name)}
                                    </option>
                                ))}
                            </TextField>
                            {errors.agent && (
                                <Box mt={3}>
                                    <FormHelperText error>
                                        {errors.agent}
                                    </FormHelperText>
                                </Box>
                            )}
                            <Box mt={2}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    size="large"
                                    fullWidth
                                    color="primary"
                                    startIcon={<EditIcon />}
                                    disabled={
                                        isSubmitting ||
                                        !hasSelectedAgent(values.agent)
                                    }
                                >
                                    {t('Buttons.AssignAgent')}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </CardContent>
        </Card>
    );
};
